import { DISCORD_ACCESS_MESSAGES } from "@volgo/core/contract";
import { type FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { useSession } from "../session";
import { Button, buttonClass } from "../ui/Button";
import { describeError } from "../ui/errors";
import { ErrorNote } from "../ui/States";

/** Where to go after logging in: the address the visitor was sent away from, if it is one of ours. */
function returnPath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : "/";
}

export function LoginPage() {
  const { state, client, loginWithKey, retry } = useSession();
  const location = useLocation();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const from = returnPath(location.state);
  const reason = client.discordRejection ?? new URLSearchParams(location.search).get("discordError");
  const discordError =
    reason && Object.hasOwn(DISCORD_ACCESS_MESSAGES, reason)
      ? DISCORD_ACCESS_MESSAGES[reason as keyof typeof DISCORD_ACCESS_MESSAGES]
      : null;

  if (state.status === "authenticated") return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await loginWithKey(key.trim());
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  // the contract's returnTo is a path of at least two characters, so "/" itself is left out
  const discord =
    from.length > 1
      ? `/api/auth/discord/start?returnTo=${encodeURIComponent(from)}`
      : "/api/auth/discord/start";

  return (
    <main className="login">
      <p className="logo">Volgo</p>
      <h1>로그인</h1>
      {discordError && (
        <p role="alert" className="note note-warn">
          {discordError}
        </p>
      )}
      {state.status === "unavailable" && <ErrorNote error={state.error} onRetry={retry} />}
      <form onSubmit={submit}>
        <label className="field">
          접근 키
          <input
            className="input"
            type="password"
            name="key"
            autoComplete="current-password"
            minLength={8}
            maxLength={256}
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </label>
        {error !== null && (
          <p role="alert" className="note note-warn">
            {describeError(error, {
              VALIDATION_FAILED: "접근 키의 모양이 맞지 않습니다. 받은 키를 그대로 붙여 넣으세요.",
            })}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy}>
          로그인
        </Button>
      </form>
      <p className="login-or">또는</p>
      <a className={buttonClass("plain")} href={discord}>
        Discord로 로그인
      </a>
      <p className="note">접근 키는 관리자가 발급합니다. 키가 없으면 관리자에게 요청하세요.</p>
    </main>
  );
}
